import { cn } from '@/lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-hover shadow-card disabled:opacity-50',
  secondary:
    'bg-surface text-ink border border-line-strong hover:bg-surface-sunken disabled:opacity-50',
  subtle: 'bg-surface-sunken text-ink-soft hover:bg-line/60 hover:text-ink disabled:opacity-50',
  ghost: 'bg-transparent text-ink-soft hover:bg-surface-sunken hover:text-ink disabled:opacity-50',
  danger:
    'bg-critical text-on-critical hover:bg-critical/90 active:bg-critical shadow-card disabled:opacity-50',
}

const SIZES: Record<ButtonSize, string> = {
  // 44px tall on lg so controls stay comfortable to tap on a phone.
  sm: 'h-8 px-2.5 text-xs gap-1.5',
  md: 'h-10 px-3.5 text-sm gap-2',
  lg: 'h-11 px-5 text-sm gap-2',
  icon: 'h-10 w-10 justify-center',
  // Icon-only affordances inside table rows and dense lists.
  'icon-sm': 'size-8 justify-center',
}

/**
 * Class strings for a button, exported so router links can adopt the same look
 * without nesting interactive elements or duplicating the variant tables.
 */
export function buttonClasses(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  className?: string,
): string {
  return cn(
    'inline-flex shrink-0 items-center justify-center rounded-[var(--radius-field)] font-medium whitespace-nowrap no-underline',
    'transition-colors duration-100',
    VARIANTS[variant],
    SIZES[size],
    className,
  )
}
